import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

/**
 * Tema chiaro / scuro.
 * ---------------------------------------------------------------------------
 * UNA classe su UN elemento: `html.theme-light` o `html.theme-dark`.
 *
 * Il portale statico aveva DUE fonti di verità in conflitto:
 *   · `html.force-light` / `html.force-dark`, scritte da `ic-bootstrap.js`
 *   · `body.theme-light` / `body.light`, che il CSS storico prendeva di mira
 * e un ponte JS (`<script id="ic-theme-sync">` in `comuni/brindisi.html`) che
 * copiava la prima sulla seconda. Qui la fonte è una.
 *
 * Default: `light`. È il default editoriale dichiarato del portale.
 *
 * La preferenza esplicita dell'utente è distinta dal default: la chiave
 * `ic-theme-user-set` esiste solo se l'utente ha scelto. Serve a non
 * "inquinare" la preferenza scrivendo il default al primo accesso — era un bug
 * corretto nel portale statico e non va reintrodotto.
 */

export type Theme = 'light' | 'dark';

const THEME_KEY = 'ic-theme';
const USER_SET_KEY = 'ic-theme-user-set';

interface ThemeContextValue {
  readonly theme: Theme;
  readonly isUserChoice: boolean;
  readonly setTheme: (theme: Theme) => void;
  readonly toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

function readStoredTheme(): { theme: Theme; isUserChoice: boolean } {
  try {
    const userSet = window.localStorage.getItem(USER_SET_KEY) === '1';
    const stored = window.localStorage.getItem(THEME_KEY);
    if (userSet && (stored === 'light' || stored === 'dark')) {
      return { theme: stored, isUserChoice: true };
    }
  } catch {
    // localStorage negato (Safari privato, kiosk in lockdown): si usa il default.
  }

  // Nessuna scelta dell'utente: si rispetta la preferenza di sistema se
  // espressa, altrimenti il default editoriale chiaro.
  const prefersDark = window.matchMedia?.('(prefers-color-scheme: dark)').matches;
  return { theme: prefersDark ? 'dark' : 'light', isUserChoice: false };
}

function applyTheme(theme: Theme): void {
  const root = document.documentElement;
  root.classList.toggle('theme-dark', theme === 'dark');
  root.classList.toggle('theme-light', theme === 'light');
  // `theme-color` del browser chrome segue il tema.
  document
    .querySelector('meta[name="theme-color"]')
    ?.setAttribute('content', theme === 'dark' ? '#0a2f6b' : '#0D47A1');
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState(readStoredTheme);

  useEffect(() => {
    applyTheme(state.theme);
  }, [state.theme]);

  // Se l'utente NON ha scelto, il tema segue la preferenza di sistema anche
  // quando cambia durante la sessione.
  useEffect(() => {
    if (state.isUserChoice) return;
    const media = window.matchMedia?.('(prefers-color-scheme: dark)');
    if (!media?.addEventListener) return;
    const onChange = (event: MediaQueryListEvent) => {
      setState({ theme: event.matches ? 'dark' : 'light', isUserChoice: false });
    };
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, [state.isUserChoice]);

  const setTheme = useCallback((theme: Theme) => {
    setState({ theme, isUserChoice: true });
    try {
      window.localStorage.setItem(THEME_KEY, theme);
      window.localStorage.setItem(USER_SET_KEY, '1');
    } catch {
      /* la scelta resta valida per la sessione */
    }
  }, []);

  const toggleTheme = useCallback(() => {
    setTheme(state.theme === 'dark' ? 'light' : 'dark');
  }, [setTheme, state.theme]);

  const value = useMemo<ThemeContextValue>(
    () => ({
      theme: state.theme,
      isUserChoice: state.isUserChoice,
      setTheme,
      toggleTheme,
    }),
    [setTheme, state.isUserChoice, state.theme, toggleTheme],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext);
  if (!context) throw new Error('useTheme richiede <ThemeProvider>');
  return context;
}
